import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, Star, StarOff, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';
import { BrandSupplierModal } from './brand-supplier-modal';

interface BrandSuppliersCardProps {
  brandId: string;
  brandName: string;
}

export function BrandSuppliersCard({ brandId, brandName }: BrandSuppliersCardProps) {
  const { t } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const utils = trpc.useUtils();

  const { data: suppliers, isLoading } = trpc.tradingBrandSupplier.getSuppliersByBrand.useQuery(brandId);

  const deleteMutation = trpc.tradingBrandSupplier.delete.useMutation({
    onSuccess: () => {
      utils.tradingBrandSupplier.getSuppliersByBrand.invalidate(brandId);
      showSuccessToast(tCommon('deleted'));
      setDeleteId(null);
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const setPrimaryMutation = trpc.tradingBrandSupplier.setPrimary.useMutation({
    onSuccess: () => {
      utils.tradingBrandSupplier.getSuppliersByBrand.invalidate(brandId);
      showSuccessToast(tCommon('updated'));
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const handleDelete = () => {
    if (deleteId) {
      deleteMutation.mutate(deleteId);
    }
  };

  const handleSetPrimary = (supplierId: string) => {
    setPrimaryMutation.mutate({ brandId, supplierId });
  };

  const existingSupplierIds = suppliers?.map((s) => s.supplierId) || [];

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div>
            <CardTitle className="text-lg">{t('brands.suppliers.title')}</CardTitle>
            <CardDescription>{t('brands.suppliers.subtitle')}</CardDescription>
          </div>
          <Button size="sm" onClick={() => setAddModalOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            {t('brands.suppliers.addSupplier')}
          </Button>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-4 text-muted-foreground">{tCommon('loading')}</div>
          ) : !suppliers || suppliers.length === 0 ? (
            <div className="text-center py-8">
              <Building2 className="mx-auto h-12 w-12 text-muted-foreground/50" />
              <p className="mt-2 text-sm font-medium">{t('brands.suppliers.noSuppliers')}</p>
              <p className="text-sm text-muted-foreground">{t('brands.suppliers.noSuppliersDesc')}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {suppliers.map((supplier) => (
                <div
                  key={supplier.id}
                  className="flex items-center justify-between p-3 border rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0">
                      <Building2 className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{supplier.companyName}</span>
                        {supplier.isPrimary && (
                          <Badge variant="default" className="text-xs">
                            <Star className="h-3 w-3 mr-1" />
                            {t('brands.suppliers.isPrimary')}
                          </Badge>
                        )}
                        {supplier.isAuthorized && (
                          <Badge variant="secondary" className="text-xs">
                            {t('brands.suppliers.isAuthorized')}
                          </Badge>
                        )}
                      </div>
                      {supplier.email && (
                        <p className="text-sm text-muted-foreground">{supplier.email}</p>
                      )}
                      {supplier.notes && (
                        <p className="text-xs text-muted-foreground mt-1">{supplier.notes}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {!supplier.isPrimary && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleSetPrimary(supplier.supplierId)}
                        disabled={setPrimaryMutation.isPending}
                        title={t('brands.suppliers.setPrimary')}
                      >
                        <StarOff className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeleteId(supplier.id)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <BrandSupplierModal
        open={addModalOpen}
        onOpenChange={setAddModalOpen}
        brandId={brandId}
        brandName={brandName}
        existingSupplierIds={existingSupplierIds}
      />

      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{tCommon('confirmDelete')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('brands.suppliers.removeConfirm')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tCommon('cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              {tCommon('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
